import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  Card,
  CardHeader,
  CardContent,
  Button,
  Checkbox,
  Chip,
  List,
  Collapse,
  Divider,
  Alert,
  useTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { LevelSelectForm } from "../../../../../../components/LevelSelectForm/LevelSelectForm.js";
import { IMPACT_LEVELS, LIKELIHOOD_LEVELS } from "../../../utils/riskUtils.js";
import { EmptyFields } from "../../../../../../utils/enums.js";
import { LinkedChecksInfo } from "../../common/LinkedChecksInfo.js";
import { RiskScoreChip } from "../../common/RiskScoreChip.js";
import ReusableAccordion from "../../../../../../components/ReusableAccordion/ReusableAccordion.js";
import FilterBar from "../../../../../../components/FilterNSearchBar/FilterNSearchBar.js";
import AccordionSkeleton from "../../../../../../components/Skeletons/AccordionSkeleton.js";

export const steps = [
  "Risk Details",
  "Map Controls & Checks",
  "Review & Add Risk",
];

export function ManualAddRiskSteps(props) {
  const theme = useTheme();
  const [expandedControlIdx, setExpandedControlIdx] = useState(null);
  const [expandedReviewAccordion, setExpandedReviewAccordion] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDomain, setSelectedDomain] = useState(null);

  const {
    step,
    formData,
    setFormData,
    errors,
    setErrors,
    selectedChecks,
    mappedControls,
    controlsData,
    riskCategories,
    controlsLoading,
    controlsError,
    updateSelectedChecksAndMappedControls,
    handleCheckToggle,
    riskCategoriesLoading,
    calculatedScore,
    mappedCount,
    selectedChecksCount,
    adminEmails,
    staffLoading,
  } = props;

  const filteredControls = controlsData?.filter((control) => {
    const matchesDomain =
      !selectedDomain || control?.control_domain === selectedDomain;
    const matchesSearch =
      control?.control_title
        ?.toLowerCase()
        .includes(searchQuery?.toLowerCase()) ||
      control?.controls_description
        ?.toLowerCase()
        .includes(searchQuery?.toLowerCase()) ||
      control?.associated_checks?.some(
        (check) =>
          check?.active_check_name
            ?.toLowerCase()
            .includes(searchQuery?.toLowerCase()) ||
          check?.active_check_description
            ?.toLowerCase()
            .includes(searchQuery?.toLowerCase()) ||
          check?.check_type?.toLowerCase().includes(searchQuery?.toLowerCase())
      );
    return matchesDomain && matchesSearch;
  });

  // Step 0: Risk Details
  if (step === 0) {
    return (
      <Box>
        <Grid container spacing={2}>
          {/* Row 1: Risk Name */}
          <Grid item xs={12}>
            <TextField
              label="Risk Name"
              value={formData?.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
              required
              error={!!errors?.name}
              helperText={errors?.name}
              placeholder="Enter a descriptive name for the risk..."
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: theme.customColors.text.main,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                  color: theme.customColors.text.secondary,
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  color: theme.customColors.text.secondary,
                },
              }}
            />
          </Grid>
          {/* Row 2: Description */}
          <Grid item xs={12}>
            <TextField
              label="Risk Description"
              value={formData?.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              fullWidth
              multiline
              rows={3}
              required
              error={!!errors?.description}
              helperText={errors?.description}
              placeholder="Provide a detailed description of the risk, including potential impacts and scenarios..."
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: "14px",
                  color: theme.customColors.text.main,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "14px",
                  color: theme.customColors.text.secondary,
                },
                "& .MuiFormHelperText-root": {
                  fontSize: "12px",
                  color: theme.customColors.text.secondary,
                },
              }}
            />
          </Grid>
          {/* Row 3: Owner (left), Category (right) */}
          <Grid item xs={12} md={6}>
            <LevelSelectForm
              label="Risk Owner Email"
              options={adminEmails.map((email) => ({
                value: email,
                label: email,
              }))}
              value={formData?.risk_owner_email}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, risk_owner_email: value }))
              }
              disabled={staffLoading}
            />
            {errors?.risk_owner_email && (
              <Typography
                variant="caption"
                color="error"
                sx={{
                  mt: 1,
                  fontSize: "12px",
                  color: theme.customColors.text.error,
                }}
              >
                {errors?.risk_owner_email}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <LevelSelectForm
              label="Risk Category"
              options={
                riskCategories?.map((cat, idx) => ({
                  value: cat?.value,
                  label: cat?.label,
                  key: cat?.value ?? idx,
                })) || []
              }
              value={formData?.category_id}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, category_id: value }))
              }
              disabled={riskCategoriesLoading}
            />
            {errors?.category_id && (
              <Typography
                variant="caption"
                color="error"
                sx={{
                  mt: 1,
                  fontSize: "12px",
                  color: theme.customColors.text.error,
                }}
              >
                {errors?.category_id}
              </Typography>
            )}
          </Grid>
          {/* Row 4: Inherent Impact (left), Inherent Likelihood (right) */}
          <Grid item xs={12} md={6}>
            <LevelSelectForm
              label="Impact"
              options={IMPACT_LEVELS}
              value={formData?.inherent_impact}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, inherent_impact: value }))
              }
              disabled={false}
            />
            {errors?.inherent_impact && (
              <Typography
                variant="caption"
                color="error"
                sx={{
                  mt: 1,
                  fontSize: "12px",
                  color: theme.customColors.text.error,
                }}
              >
                {errors?.inherent_impact}
              </Typography>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <LevelSelectForm
              label="Likelihood"
              options={LIKELIHOOD_LEVELS}
              value={formData?.inherent_likelihood}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, inherent_likelihood: value }))
              }
              disabled={false}
            />
            {errors?.inherent_likelihood && (
              <Typography
                variant="caption"
                color="error"
                sx={{
                  mt: 1,
                  fontSize: "12px",
                  color: theme.customColors.text.error,
                }}
              >
                {errors?.inherent_likelihood}
              </Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Step 1: Map Controls & Checks
  if (step === 1) {
    {
      controlsError && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: 300,
            background: theme.customColors.background.main,
          }}
        >
          <Typography color={theme.customColors.text.error}>
            Failed to load controls.
          </Typography>
        </Box>
      );
    }
    return (
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 2px 8px #00000010",
          border: `1px solid ${theme.customColors.border.main}`,
          background: theme.customColors.background.card,
        }}
      >
        <CardHeader
          title={
            <Typography
              variant="h5"
              fontWeight={700}
              color={theme.customColors.text.strong}
            >
              Map Controls & Checks
            </Typography>
          }
          subheader={
            <Typography
              variant="body2"
              color={theme.customColors.text.secondary}
            >
              Search, filter, and map controls to this risk. Expand a control to
              select relevant checks.{" "}
              <strong>At least one check must be selected to proceed.</strong>
            </Typography>
          }
          sx={{
            borderBottom: `1px solid ${theme.customColors.border.main}`,
            background: theme.customColors.background.cardHeader,
            pb: 1,
          }}
        />
        {controlsLoading && <AccordionSkeleton />}
        <CardContent sx={{ p: 2 }}>
          {errors?.checks && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: theme.customColors.status.error.bg,
                border: `1px solid ${theme.customColors.border.error}`,
              }}
            >
              {errors?.checks}
            </Alert>
          )}
          <FilterBar
            searchQuery={searchQuery}
            onSearchChange={(e) => setSearchQuery(e.target.value)}
            filterLabel="Filter by Domain"
            filters={Array.from(
              new Set(
                controlsData?.map((c) => c?.control_domain)?.filter(Boolean)
              )
            )}
            selectedFilter={selectedDomain}
            onFilterChange={(domain) => {
              setSelectedDomain(domain);
              setExpandedControlIdx(null);
            }}
            onClearFilters={() => {
              setSearchQuery("");
              setSelectedDomain(null);
              setExpandedControlIdx(null);
            }}
            countLabel={`${filteredControls?.length} controls found, ${mappedCount} mapped, ${selectedChecksCount} checks selected`}
          />
          <Box>
            {filteredControls?.length === 0 ? (
              <Typography
                variant="body2"
                color={theme.customColors.text.secondary}
                sx={{ textAlign: "center", py: 6 }}
              >
                {"No controls found. Try adjusting your search or filters."}
              </Typography>
            ) : (
              filteredControls?.map((control, idx) => (
                <Paper
                  key={control?.controls_id}
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: mappedControls?.some(
                      (c) => c?.controls_id === control?.controls_id
                    )
                      ? `2px solid ${theme.palette._blue.main}`
                      : `1px solid ${theme.customColors.border.main}`,
                    background: mappedControls?.some(
                      (c) => c?.controls_id === control?.controls_id
                    )
                      ? theme.customColors.background.info
                      : theme.customColors.background.card,
                    transition: "all 0.2s",
                    boxShadow: mappedControls?.some(
                      (c) => c?.controls_id === control?.controls_id
                    )
                      ? "0 2px 8px #3B82F620"
                      : "none",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color={theme.customColors.text.strong}
                      >
                        {control?.controls_name} : {control?.control_title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color={theme.customColors.text.secondary}
                        sx={{ fontSize: "13px" }}
                      >
                        {control?.control_domain}
                      </Typography>
                    </Box>
                    <Button
                      variant="text"
                      size="small"
                      onClick={() =>
                        setExpandedControlIdx(
                          expandedControlIdx === idx ? null : idx
                        )
                      }
                      sx={{ fontSize: "10px", minWidth: "auto", p: 0.5 }}
                    >
                      {expandedControlIdx === idx ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </Button>
                  </Box>
                  {expandedControlIdx === idx && <Divider />}
                  <Collapse
                    in={expandedControlIdx === idx}
                    timeout="auto"
                    unmountOnExit
                  >
                    <Box
                      sx={{
                        p: 2,
                        background: theme.customColors.background.card,
                        borderRadius: 2,
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Checkbox
                          checked={
                            (control?.associated_checks || []).every((check) =>
                              selectedChecks?.includes(check?.active_check_id)
                            ) && (control?.associated_checks || [])?.length > 0
                          }
                          indeterminate={
                            (control?.associated_checks || []).some((check) =>
                              selectedChecks?.includes(check?.active_check_id)
                            ) &&
                            !(control?.associated_checks || []).every((check) =>
                              selectedChecks?.includes(check?.active_check_id)
                            )
                          }
                          onChange={(_, checked) => {
                            const checkIds = (
                              control?.associated_checks || []
                            ).map((check) => check?.active_check_id);
                            let newSelected;
                            if (checked) {
                              newSelected = Array.from(
                                new Set([
                                  ...(selectedChecks || []),
                                  ...(checkIds || []),
                                ])
                              );
                            } else {
                              newSelected = (selectedChecks || [])?.filter(
                                (id) => !(checkIds || [])?.includes(id)
                              );
                            }
                            updateSelectedChecksAndMappedControls(newSelected);
                          }}
                          size="small"
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.customColors.text.secondary,
                            fontSize: "0.875rem",
                            mb: 0.5,
                          }}
                        >
                          Select All
                        </Typography>
                      </Box>
                      <List sx={{ p: 0 }}>
                        {(control?.associated_checks || []).map(
                          (check, index) => (
                            <Paper
                              key={check?.active_check_id}
                              elevation={
                                selectedChecks?.includes(check?.active_check_id)
                                  ? 3
                                  : 0
                              }
                              sx={{
                                mb: 0.5,
                                border: selectedChecks?.includes(
                                  check?.active_check_id
                                )
                                  ? `2px solid ${theme.palette._blue.main}`
                                  : `1px solid ${theme.customColors.border.main}`,
                                borderLeft: selectedChecks?.includes(
                                  check?.active_check_id
                                )
                                  ? "6px solid #3B82F6"
                                  : "1px solid #E5E7EB",
                                background: selectedChecks?.includes(
                                  check?.active_check_id
                                )
                                  ? theme.customColors.background.info
                                  : index % 2 === 0
                                    ? theme.customColors.background.light
                                    : theme.customColors.background.card,
                                transition: "all 0.2s",
                                "&:hover": {
                                  boxShadow: "0 4px 16px rgba(59,130,246,0.08)",
                                  borderColor: theme.palette._blue.main,
                                },
                                p: 1,
                                display: "flex",
                                flexDirection: "column",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  checked={selectedChecks?.includes(
                                    check?.active_check_id
                                  )}
                                  onChange={() =>
                                    handleCheckToggle?.(
                                      check?.active_check_id,
                                      control
                                    )
                                  }
                                  size="small"
                                />
                                <Chip
                                  label={
                                    check?.active_check_name
                                      ?.replace(/_/g, " ")
                                      .toUpperCase() || EmptyFields.UNKNOWN
                                  }
                                  size="small"
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.8125rem",
                                    textTransform: "uppercase",
                                    borderRadius: "8px",
                                    backgroundColor:
                                      theme.customColors.background.light,
                                    color: theme.customColors.text.main,
                                    letterSpacing: 0.5,
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                color={theme.customColors.text.secondary}
                                sx={{
                                  fontSize: "0.875rem",
                                  lineHeight: 1.6,
                                  mt: 0.5,
                                  pl: 5,
                                  fontWeight: 400,
                                  wordBreak: "break-word",
                                }}
                              >
                                {check?.active_check_description}
                              </Typography>
                            </Paper>
                          )
                        )}
                      </List>
                    </Box>
                  </Collapse>
                </Paper>
              ))
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Step 2: Review & Add Risk
  if (step === 2) {
    return (
      <Box
        sx={{
          background: theme.customColors.background.main,
          borderRadius: 3,
          p: { xs: 1, sm: 2 },
          minHeight: 400,
        }}
      >
        {/* Summary Row */}
        <Paper
          elevation={1}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: theme.customColors.background.card,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              sx={{
                fontSize: "1.1rem",
                mb: 0.5,
                display: "flex",
                alignItems: "center",
              }}
            >
              {formData?.name}
            </Typography>
            <Typography
              variant="body2"
              color={theme.customColors.text.secondary}
              sx={{ fontSize: "13px" }}
            >
              {formData?.description}
            </Typography>
          </Box>
          <Chip
            label={`Controls: ${mappedControls?.length}`}
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              height: 32,
              background: theme.customColors.status.success.bg,
              color: theme.customColors.status.success.text,
              borderRadius: 1,
            }}
          />
          <Chip
            label={`Checks: ${selectedChecks?.length}`}
            sx={{
              fontWeight: 600,
              fontSize: "13px",
              height: 32,
              background: theme.palette._blue.light,
              color: theme.customColors.text.secondary,
              borderRadius: 1,
            }}
          />
        </Paper>
        {/* Risk Information Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: theme.customColors.background.card,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
          >
            {" "}
            Risk Information{" "}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="caption"
                color={theme.customColors.text.secondary}
              >
                Category
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontSize: "14px",
                  mt: 0.5,
                  color: theme.customColors.text.main,
                }}
              >
                {formData?.category_title ||
                  riskCategories?.find(
                    (cat) => cat?.value === formData?.category_id
                  )?.label ||
                  ""}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="caption"
                color={theme.customColors.text.secondary}
              >
                Owner
              </Typography>
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{
                  fontSize: "14px",
                  mt: 0.5,
                  color: theme.customColors.text.main,
                }}
              >
                {formData?.risk_owner_email}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box display="flex" flexDirection="column">
                <Typography
                  variant="caption"
                  color={theme.customColors.text.secondary}
                >
                  Risk Score
                </Typography>
                <RiskScoreChip
                  score={calculatedScore}
                  sx={{ width: "fit-content" }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="caption"
                color={theme.customColors.text.secondary}
              >
                Impact Level
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  mt: 0.5,
                  color: theme.customColors.text.main,
                }}
              >
                {formData?.inherent_impact} -{" "}
                {
                  IMPACT_LEVELS?.find(
                    (l) => l?.value === formData?.inherent_impact
                  )?.label?.split(" - ")[1]
                }
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Typography
                variant="caption"
                color={theme.customColors.text.secondary}
              >
                Likelihood Level
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "14px",
                  mt: 0.5,
                  color: theme.customColors.text.main,
                }}
              >
                {formData?.inherent_likelihood} -{" "}
                {
                  LIKELIHOOD_LEVELS?.find(
                    (l) => l?.value === formData?.inherent_likelihood
                  )?.label?.split(" - ")[1]
                }
              </Typography>
            </Grid>
          </Grid>
        </Paper>
        {/* Selected Checks Section */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            background: theme.customColors.background.card,
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
          >
            Selected Controls & Checks ({mappedControls?.length})
          </Typography>
          {selectedChecks?.length > 0 ? (
            <Box>
              {mappedControls
                ?.map((control, idx) => {
                  const selectedControlChecks = (
                    control?.associated_checks || []
                  )?.filter((check) =>
                    selectedChecks?.includes(check?.active_check_id)
                  );
                  if (selectedControlChecks?.length === 0) return null;
                  return (
                    <ReusableAccordion
                      key={mappedControls?.[idx]?.controls_id}
                      expanded={expandedReviewAccordion === idx}
                      onChange={() =>
                        setExpandedReviewAccordion(
                          expandedReviewAccordion === idx ? null : idx
                        )
                      }
                      detailsSx={{
                        pl: 0,
                        pt: 2,
                        background: theme.customColors.background.light,
                        borderRadius: 2,
                      }}
                      summary={
                        <>
                          {idx + 1}. {control?.controls_name} :{" "}
                          {control?.control_title}
                        </>
                      }
                      details={
                        selectedControlChecks?.length > 0 ? (
                          <LinkedChecksInfo control={control} />
                        ) : (
                          <Typography
                            variant="body2"
                            color={theme.customColors.text.secondary}
                            sx={{ p: 2 }}
                          >
                            {"No checks mapped to this control."}
                          </Typography>
                        )
                      }
                    />
                  );
                })
                ?.filter(Boolean)}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color={theme.customColors.text.secondary}
              sx={{ fontSize: "12px" }}
            >
              No checks selected
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  return null;
}
